import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { NotificationEntry } from '@/mocks/listenerCenter';

type NotificationType = NotificationEntry['type'];
type Tab = 'all' | 'unread' | 'archived';

interface Props {
  notifications: NotificationEntry[];
}

interface PreferencesState {
  new_transmission: boolean;
  new_event: boolean;
  contest: boolean;
  radio_message: boolean;
  upcoming_live: boolean;
  sound: boolean;
}

const TYPE_CONFIG: Record<NotificationType, { label: string; icon: string; color: string }> = {
  new_transmission: { label: 'Nueva transmisión', icon: 'ri-broadcast-line', color: 'bg-emerald-100 text-emerald-700' },
  new_event: { label: 'Nuevo evento', icon: 'ri-calendar-event-line', color: 'bg-amber-100 text-amber-700' },
  contest: { label: 'Concurso', icon: 'ri-trophy-line', color: 'bg-rose-100 text-rose-700' },
  radio_message: { label: 'Mensaje radio', icon: 'ri-message-2-line', color: 'bg-primary-100 text-primary-700' },
  upcoming_live: { label: 'Live próximo', icon: 'ri-live-line', color: 'bg-sky-100 text-sky-700' },
};

export default function NotificationsList({ notifications }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState(notifications);
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<PreferencesState>({
    new_transmission: true,
    new_event: true,
    contest: true,
    radio_message: true,
    upcoming_live: true,
    sound: false,
  });

  const allCount = items.filter((n) => !n.isArchived).length;
  const unreadCount = items.filter((n) => !n.isRead && !n.isArchived).length;
  const archivedCount = items.filter((n) => n.isArchived).length;

  const displayedItems = (() => {
    let filtered = [...items];
    if (activeTab === 'unread') filtered = filtered.filter((n) => !n.isRead && !n.isArchived);
    else if (activeTab === 'archived') filtered = filtered.filter((n) => n.isArchived);
    else filtered = filtered.filter((n) => !n.isArchived);
    return filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  })();

  const markAsRead = (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  };

  const markAllRead = () => {
    Swal.fire({
      title: 'Marcar todo como leído',
      text: '¿Querés marcar todas las notificaciones como leídas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, marcar todo',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Todas las notificaciones fueron marcadas como leídas',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const archiveNotification = (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isArchived: true } : n)));
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: 'Notificación archivada',
      icon: 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
      buttonsStyling: false,
    });
  };

  const unarchiveNotification = (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isArchived: false } : n)));
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: 'Notificación restaurada',
      icon: 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
      buttonsStyling: false,
    });
  };

  const deleteNotification = (id: number, title: string) => {
    Swal.fire({
      title: 'Eliminar notificación',
      text: `¿Querés eliminar permanentemente "${title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems((prev) => prev.filter((n) => n.id !== id));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Notificación eliminada',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const deleteAllArchived = () => {
    Swal.fire({
      title: 'Vaciar archivadas',
      text: '¿Querés eliminar permanentemente todas las notificaciones archivadas? Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, vaciar todo',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems((prev) => prev.filter((n) => !n.isArchived));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Archivadas eliminadas',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const savePreferences = () => {
    Swal.fire({
      title: 'Preferencias guardadas',
      text: 'Tus preferencias de notificaciones se actualizaron correctamente.',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#10b981',
      timer: 2500,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
    setPrefsOpen(false);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffHrs = Math.floor((now.getTime() - d.getTime()) / 3600000);
    if (diffHrs < 1) return 'Ahora';
    if (diffHrs < 24) return `Hace ${diffHrs}h`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'all', label: 'Todas', count: allCount },
    { key: 'unread', label: 'No leídas', count: unreadCount },
    { key: 'archived', label: 'Archivadas', count: archivedCount },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground-950">Notificaciones</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-[10px] font-bold rounded-full">{unreadCount}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && activeTab !== 'archived' && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Marcar todo leído
            </button>
          )}
          <button
            onClick={() => setPrefsOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-settings-3-line text-sm"></i>
            Preferencias
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-background-100 rounded-full p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-background-50 text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {tab.label}
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === tab.key ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
        {archivedCount > 0 && activeTab === 'archived' && (
          <button
            onClick={deleteAllArchived}
            className="ml-2 px-3 py-1.5 rounded-full text-[10px] font-semibold text-rose-600 hover:bg-rose-50 cursor-pointer whitespace-nowrap transition-all"
          >
            <i className="ri-delete-bin-line mr-1"></i>
            Vaciar todo
          </button>
        )}
      </div>

      {/* Empty State */}
      {displayedItems.length === 0 && (
        <div className="bg-background-50 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className={`text-2xl text-foreground-400 ${activeTab === 'archived' ? 'ri-archive-line' : 'ri-notification-3-line'}`}></i>
          </div>
          <p className="text-foreground-600 text-sm">
            {activeTab === 'archived' ? 'No tenés notificaciones archivadas' : activeTab === 'unread' ? 'No tenés notificaciones sin leer' : 'No tenés notificaciones'}
          </p>
          <p className="text-foreground-400 text-xs mt-1">
            {activeTab === 'archived' ? 'Las notificaciones que archives aparecerán acá' : activeTab === 'unread' ? 'Estás al día con todas tus notificaciones' : 'Acá van a aparecer tus avisos y novedades'}
          </p>
        </div>
      )}

      {/* Notifications List */}
      {displayedItems.length > 0 && (
        <div className="space-y-1.5">
          {displayedItems.map((notif) => {
            const cfg = TYPE_CONFIG[notif.type];
            return (
              <div
                key={notif.id}
                className={`relative bg-background-50 rounded-xl px-4 py-3.5 flex items-start gap-3 cursor-pointer hover:bg-background-100/80 transition-all group ${
                  !notif.isRead && !notif.isArchived ? 'ring-1 ring-primary-200/60' : ''
                } ${notif.isArchived ? 'opacity-60' : ''}`}
                onClick={() => {
                  if (!notif.isRead && !notif.isArchived) markAsRead(notif.id);
                  if (notif.actionUrl && notif.actionUrl !== '/listener') {
                    navigate(notif.actionUrl);
                  }
                }}
              >
                {!notif.isRead && !notif.isArchived && (
                  <div className="absolute top-3.5 left-3 w-2 h-2 bg-primary-500 rounded-full"></div>
                )}
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.color}`}>
                  <i className={cfg.icon}></i>
                </div>
                <div className={`flex-1 min-w-0 ${!notif.isRead && !notif.isArchived ? 'ml-1' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">{cfg.label}</span>
                    {notif.isArchived && (
                      <span className="px-1.5 py-0.5 bg-foreground-100 text-foreground-500 rounded text-[9px] font-medium whitespace-nowrap">
                        Archivada
                      </span>
                    )}
                  </div>
                  <h4 className={`text-sm mt-0.5 ${!notif.isRead && !notif.isArchived ? 'font-bold text-foreground-950' : 'font-semibold text-foreground-800'}`}>
                    {notif.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{notif.message}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-foreground-400">{formatTime(notif.timestamp)}</span>
                    {notif.stationName && (
                      <>
                        <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                        <span className="text-[10px] text-foreground-500">{notif.stationName}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                  {!notif.isRead && !notif.isArchived && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-all"
                      title="Marcar como leída"
                    >
                      <i className="ri-check-double-line"></i>
                    </button>
                  )}
                  {!notif.isArchived ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); archiveNotification(notif.id); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                      title="Archivar"
                    >
                      <i className="ri-archive-line"></i>
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); unarchiveNotification(notif.id); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-all"
                      title="Restaurar"
                    >
                      <i className="ri-inbox-unarchive-line"></i>
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id, notif.title); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer transition-all"
                    title="Eliminar"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preferences Modal */}
      {prefsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setPrefsOpen(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Preferencias de notificaciones</h3>
              <button
                onClick={() => setPrefsOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <p className="text-xs text-foreground-500 mb-5">
              Elegí qué tipo de notificaciones querés recibir. Podés cambiarlas cuando quieras.
            </p>

            <div className="space-y-3 mb-6">
              {(Object.keys(TYPE_CONFIG) as NotificationType[]).map((type) => {
                const cfg = TYPE_CONFIG[type];
                return (
                  <label
                    key={type}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-background-100 cursor-pointer transition-all"
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.color}`}>
                      <i className={cfg.icon}></i>
                    </div>
                    <span className="flex-1 text-sm font-medium text-foreground-800">{cfg.label}</span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setPrefs((p) => ({ ...p, [type]: !p[type] }));
                      }}
                      className={`relative w-10 h-5 rounded-full transition-all cursor-pointer ${
                        prefs[type] ? 'bg-primary-500' : 'bg-foreground-200'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 shadow-sm transition-all ${
                          prefs[type] ? 'left-5' : 'left-0.5'
                        }`}
                      ></div>
                    </button>
                  </label>
                );
              })}

              {/* Sound toggle */}
              <div className="border-t border-background-200 pt-3 mt-3">
                <label className="flex items-center gap-3 p-3 rounded-xl hover:bg-background-100 cursor-pointer transition-all">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-foreground-100 text-foreground-600">
                    <i className="ri-volume-up-line"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground-800 block">Sonido</span>
                    <span className="text-[10px] text-foreground-400">Reproducir sonido al recibir notificaciones</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setPrefs((p) => ({ ...p, sound: !p.sound }));
                    }}
                    className={`relative w-10 h-5 rounded-full transition-all cursor-pointer ${
                      prefs.sound ? 'bg-primary-500' : 'bg-foreground-200'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 shadow-sm transition-all ${
                        prefs.sound ? 'left-5' : 'left-0.5'
                      }`}
                    ></div>
                  </button>
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPrefsOpen(false)}
                className="flex-1 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={savePreferences}
                className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}